/** D&D 5e rules helpers shared by the server, bot and web table. */
export const ABILITIES = ["str", "dex", "con", "int", "wis", "cha"];
export const ABILITY_NAMES = {
    str: "Strength",
    dex: "Dexterity",
    con: "Constitution",
    int: "Intelligence",
    wis: "Wisdom",
    cha: "Charisma",
};
export const SKILLS = {
    acrobatics: "dex",
    animal_handling: "wis",
    arcana: "int",
    athletics: "str",
    deception: "cha",
    history: "int",
    insight: "wis",
    intimidation: "cha",
    investigation: "int",
    medicine: "wis",
    nature: "int",
    perception: "wis",
    performance: "cha",
    persuasion: "cha",
    religion: "int",
    sleight_of_hand: "dex",
    stealth: "dex",
    survival: "wis",
};
export const SKILL_KEYS = Object.keys(SKILLS);
export const SKILL_NAMES = Object.fromEntries(SKILL_KEYS.map((k) => [k, k.split("_").map((w) => w[0].toUpperCase() + w.slice(1)).join(" ")]));
SKILL_NAMES.sleight_of_hand = "Sleight of Hand";
export const isAbility = (v) => typeof v === "string" && ABILITIES.includes(v);
export const isSkill = (v) => typeof v === "string" && v in SKILLS;
/** Ability modifier: floor((score - 10) / 2). */
export function abilityMod(score) {
    return Math.floor((score - 10) / 2);
}
/** "+3", "-1", "+0" */
export function formatMod(mod) {
    return mod >= 0 ? `+${mod}` : `${mod}`;
}
/** Standard 5e proficiency bonus by character level (1-20). */
export function proficiencyForLevel(level) {
    const l = Math.min(20, Math.max(1, Math.floor(level)));
    return 2 + Math.floor((l - 1) / 4);
}
/** Jack of All Trades: half proficiency (rounded down) on ability checks you're not proficient in. */
const joat = (sheet) => (sheet.details?.jackOfAllTrades ? Math.floor(sheet.proficiencyBonus / 2) : 0);
/** Modifier for a check/save/skill/initiative roll from a character sheet. */
export function checkModifier(sheet, check) {
    switch (check.kind) {
        case "ability":
            return isAbility(check.key) ? abilityMod(sheet.abilities[check.key]) + joat(sheet) : 0;
        case "save":
            if (!isAbility(check.key))
                return 0;
            return (abilityMod(sheet.abilities[check.key]) +
                (sheet.saveProficiencies.includes(check.key) ? sheet.proficiencyBonus : 0));
        case "skill": {
            if (!isSkill(check.key))
                return 0;
            const ability = SKILLS[check.key];
            const base = abilityMod(sheet.abilities[ability]);
            if (sheet.details?.expertise?.includes(check.key))
                return base + 2 * sheet.proficiencyBonus;
            if (sheet.skillProficiencies.includes(check.key))
                return base + sheet.proficiencyBonus;
            return base + joat(sheet);
        }
        case "initiative":
            return abilityMod(sheet.abilities.dex) + joat(sheet) + (sheet.details?.initiativeMisc ?? 0);
        case "death":
            return 0;
    }
}
/** Human label, e.g. "Perception check", "DEX save", "Strength check", "Initiative". */
export function checkLabel(check) {
    switch (check.kind) {
        case "ability":
            return `${isAbility(check.key) ? ABILITY_NAMES[check.key] : "Ability"} check`;
        case "save":
            return `${isAbility(check.key) ? check.key.toUpperCase() : ""} saving throw`.trim();
        case "skill":
            return `${isSkill(check.key) ? SKILL_NAMES[check.key] : "Skill"} check`;
        case "initiative":
            return "Initiative";
        case "death":
            return "Death saving throw";
    }
}
/** Parse loose user/LLM input like "perception", "dex save", "strength" into a CheckSpec. */
export function parseCheck(input) {
    // Models sometimes quote the name ("\"Persuasion\"", 'Insight', [Perception]): strip quotes and brackets first.
    const s = input.replace(/["'“”‘’`\[\](){}]/g, " ").trim().toLowerCase().replace(/[\s-]+/g, "_").replace(/_?(check|throw|saving)$/g, "");
    if (!s)
        return null;
    if (s === "initiative")
        return { kind: "initiative" };
    if (s.startsWith("death"))
        return { kind: "death" };
    const ab = (t) => {
        if (isAbility(t))
            return t;
        const found = ABILITIES.find((a) => ABILITY_NAMES[a].toLowerCase() === t);
        return found ?? null;
    };
    const save = /^(.+?)_?(save|saving_throw|saving)$/.exec(s);
    if (save) {
        const a = ab(save[1]);
        return a ? { kind: "save", key: a } : null;
    }
    if (isSkill(s))
        return { kind: "skill", key: s };
    const a = ab(s);
    if (a)
        return { kind: "ability", key: a };
    return null;
}
//# sourceMappingURL=dnd.js.map